import React from "react";
import { gstDay } from "../../../../shared/utils/gstApplicability";
import {
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherInfo,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { Avatar } from "../../../../ui/components/Avatar";
import { Button } from "../../../../ui/components/Button";
import { Loader } from "../../../../ui/components/Loader";
import { Table } from "../../../../ui/components/Table";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import { formatDate, truncateText } from "../../../../shared/utils/formatters";
import { InvoiceEntity, PaginationInfo } from "../../domain";
import { InvoiceStatusChip } from "./InvoiceStatusChip";

interface InvoiceTableProps {
  invoices: InvoiceEntity[];
  loading: boolean;
  inlineLoading?: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  itemsPerPage: number;
  searchTerm: string;
  onReview: (invoice: InvoiceEntity) => void;
  onPageChange: (page: number) => void;
}

export const InvoiceTable: React.FC<InvoiceTableProps> = ({
  invoices,
  loading,
  inlineLoading = false,
  error,
  pagination,
  itemsPerPage,
  searchTerm,
  onReview,
  onPageChange,
}) => {
  return (
    <div className="flex w-full flex-col items-start">
      <Table
        header={
          <Table.HeaderRow>
            <Table.HeaderCell>Invoice Number</Table.HeaderCell>
            <Table.HeaderCell>Customer</Table.HeaderCell>
            <Table.HeaderCell>Amount</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Source</Table.HeaderCell>
            <Table.HeaderCell>Uploaded Date</Table.HeaderCell>
            <Table.HeaderCell />
          </Table.HeaderRow>
        }
      >
        {loading ? (
          <TableSkeletonRows rowCount={itemsPerPage} columnCount={7} />
        ) : inlineLoading ? (
          <Table.Row>
            <Table.Cell colSpan={7}>
              <div className="flex w-full items-center justify-center py-8">
                <Loader />
              </div>
            </Table.Cell>
          </Table.Row>
        ) : error ? (
          <Table.Row>
            <Table.Cell colSpan={7}>
              <div className="flex w-full items-center justify-center py-8">
                <span className="text-body font-body text-error-600">
                  {error}
                </span>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : invoices.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={7}>
              <div className="flex w-full items-center justify-center py-8">
                <span className="text-body font-body text-neutral-500">
                  {searchTerm.trim()
                    ? "No invoices match your search"
                    : "No invoices found"}
                </span>
              </div>
            </Table.Cell>
          </Table.Row>
        ) : (
          invoices.map((invoice) => (
            <InvoiceTableRow
              key={invoice.id}
              invoice={invoice}
              onReview={onReview}
            />
          ))
        )}
      </Table>

      {!loading && !inlineLoading && pagination && (
        <InvoicePagination
          pagination={pagination}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

interface InvoiceTableRowProps {
  invoice: InvoiceEntity;
  onReview: (invoice: InvoiceEntity) => void;
}

const InvoiceTableRow: React.FC<InvoiceTableRowProps> = ({
  invoice,
  onReview,
}) => {
  const customerName = getDocumentName(invoice);

  return (
    <Table.Row>
      <Table.Cell>
        <div className="flex flex-col items-start gap-1">
          <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
            {truncateText(getDocumentNumber(invoice), 24)}
          </span>
          <span className="text-caption font-caption text-subtext-color">
            Issued on {getIssueDateLabel(invoice)}
          </span>
        </div>
      </Table.Cell>
      <Table.Cell>
        <div className="flex items-center gap-2">
          <Avatar size="small" square={true}>
            {customerName.charAt(0).toUpperCase()}
          </Avatar>
          <span className="max-w-[240px] truncate whitespace-nowrap text-body font-body text-default-font">
            {truncateText(customerName, 35)}
          </span>
        </div>
      </Table.Cell>
      <Table.Cell>
        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
          {getDocumentAmount(invoice)}
        </span>
      </Table.Cell>
      <Table.Cell>
        <div className="flex flex-col items-start gap-1">
          <div className="flex flex-nowrap items-center gap-2">
            <InvoiceStatusChip invoice={invoice} />
            {invoice.write_off_status === "written_off" && (
              <span className="shrink-0 rounded-md border border-solid border-neutral-200 bg-neutral-100 px-2 py-0.5 text-caption font-caption text-neutral-600">
                Written off
              </span>
            )}
          </div>
          {invoice.updated_at && (
            <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
              {getStatusDateLabel(invoice)}
            </span>
          )}
        </div>
      </Table.Cell>
      <Table.Cell>
        <SourceCell
          source={getSourceText(invoice)}
          fileName={invoice.original_filename || "N/A"}
        />
      </Table.Cell>
      <Table.Cell>
        <span className="whitespace-nowrap text-body font-body text-subtext-color">
          {formatDateOrNA(invoice.created_at)}
        </span>
      </Table.Cell>
      <Table.Cell>
        <Button variant="neutral-primary" onClick={() => onReview(invoice)}>
          Review
        </Button>
      </Table.Cell>
    </Table.Row>
  );
};

interface SourceCellProps {
  source: string;
  fileName: string;
}

const SourceCell: React.FC<SourceCellProps> = ({ source, fileName }) => (
  <div className="flex items-center gap-1">
    <span className="whitespace-nowrap text-body font-body text-neutral-500">
      {source}
    </span>
    <SubframeCore.Tooltip.Provider>
      <SubframeCore.Tooltip.Root>
        <SubframeCore.Tooltip.Trigger asChild={true}>
          <FeatherInfo className="cursor-pointer text-body font-body text-neutral-400" />
        </SubframeCore.Tooltip.Trigger>
        <SubframeCore.Tooltip.Portal>
          <SubframeCore.Tooltip.Content
            side="top"
            align="center"
            sideOffset={4}
            asChild={true}
          >
            <div className="flex items-center gap-2 rounded-md bg-neutral-900 px-3 py-2 shadow-lg">
              <span className="max-w-[280px] truncate text-caption font-caption text-white">
                {fileName}
              </span>
            </div>
          </SubframeCore.Tooltip.Content>
        </SubframeCore.Tooltip.Portal>
      </SubframeCore.Tooltip.Root>
    </SubframeCore.Tooltip.Provider>
  </div>
);

interface InvoicePaginationProps {
  pagination: PaginationInfo;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const InvoicePagination: React.FC<InvoicePaginationProps> = ({
  pagination,
  itemsPerPage,
  onPageChange,
}) => {
  const currentPage = pagination.current_page || 1;
  const totalPages = pagination.total_pages || 0;

  if (totalPages <= 1) return null;

  return (
    <div className="flex w-full items-center justify-between mt-4 flex-shrink-0">
      <span className="text-body font-body text-subtext-color">
        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
        {Math.min(currentPage * itemsPerPage, pagination.total)} of{" "}
        {pagination.total} results
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          icon={<FeatherChevronLeft />}
        >
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {getVisiblePages(currentPage, totalPages).map((pageNumber) => (
            <Button
              key={pageNumber}
              variant={
                currentPage === pageNumber
                  ? "brand-primary"
                  : "neutral-tertiary"
              }
              size="small"
              onClick={() => onPageChange(pageNumber)}
              className="min-w-[32px]"
            >
              {pageNumber}
            </Button>
          ))}
        </div>
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!pagination.has_more}
          icon={<FeatherChevronRight />}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const getVisiblePages = (currentPage: number, totalPages: number) => {
  return Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
    if (totalPages <= 5) return index + 1;
    if (currentPage <= 3) return index + 1;
    if (currentPage >= totalPages - 2) return totalPages - 4 + index;
    return currentPage - 2 + index;
  });
};

type InvoiceWithResponseDates = InvoiceEntity & {
  date?: string | null;
  invoice_date?: string | null;
  issue_date?: string | null;
};

const getDocumentAmount = (invoice: InvoiceEntity): string => {
  const total = invoice.invoice_json?.total;
  if (total !== undefined && total !== null) {
    const numericTotal = typeof total === "number" ? total : Number(total);
    const displayTotal = Number.isFinite(numericTotal)
      ? numericTotal.toLocaleString("en-IN")
      : String(total);

    return `${invoice.invoice_json.currency_symbol || ""}${displayTotal}`;
  }

  return "N/A";
};

const getDocumentName = (invoice: InvoiceEntity): string => {
  if (invoice.invoice_json?.customer_id !== "") {
    return invoice.invoice_json?.customer_name || "Unknown Customer";
  }

  return "Unknown Customer";
};

const getDocumentNumber = (invoice: InvoiceEntity): string => {
  return invoice.invoice_json?.invoice_number || invoice.id.toString();
};

const getSourceText = (invoice: InvoiceEntity): string => {
  const source = invoice.source || "upload";
  return source.charAt(0).toUpperCase() + source.slice(1);
};

const getIssueDateLabel = (invoice: InvoiceEntity): string => {
  const responseInvoice = invoice as InvoiceWithResponseDates;
  // Prefer document `date` over legacy `invoice_date` (often stale create-day).
  const date =
    getNonEmptyString(invoice.invoice_json?.date) ||
    getNonEmptyString(responseInvoice.date) ||
    getNonEmptyString(invoice.invoice_json?.invoice_date) ||
    getNonEmptyString(responseInvoice.invoice_date) ||
    getNonEmptyString(responseInvoice.issue_date);

  return formatDateOrNA(date);
};

const getNonEmptyString = (value: unknown): string | null => {
  return typeof value === "string" && value.trim() ? value.trim() : null;
};

const formatDateOrNA = (dateString?: string | null): string => {
  if (!dateString) return "N/A";

  // ZOUR-19 (review 15): normalise first. Documents captured before the
  // transfer started writing the `date` column fall back to the JSON date,
  // which is the extractor's DD/MM/YYYY — `new Date("15/05/2026")` is Invalid
  // Date, so this cell read "N/A" on a document that plainly has a date on it.
  // gstDay() is the codebase's day-first parser; using it here keeps one
  // reading of a date rather than a second, subtly different one.
  const normalised = gstDay(dateString) ?? dateString;

  const date = new Date(normalised);
  if (Number.isNaN(date.getTime())) return "N/A";

  return formatDate(normalised, "medium");
};

const getStatusDateLabel = (invoice: InvoiceEntity): string => {
  if (invoice.status === "4") {
    return `Rejected at ${formatDate(invoice.updated_at, "medium")}`;
  }

  if (invoice.status === "3") {
    return `Duplicated at ${formatDate(invoice.updated_at, "medium")}`;
  }

  return `Modified ${formatDate(invoice.updated_at, "medium")}`;
};
